import { forwardRef, isValidElement, useCallback } from 'react';
import { useSnackbar, SnackbarContent } from 'notistack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { CardContent, Stack, styled } from '@mui/material';
import SuccessIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/ErrorOutlineRounded';
import { ReactComponent as WarningIcon } from 'assets/images/alert.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { SvgIcon } from '@mui/material';

const IconVariants = {
  success: SuccessIcon,
  error: ErrorIcon,
  warning: WarningIcon,
  info: InfoIcon,
  custom: InfoIcon,
};

const TitleVariants = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: ' ',
};

const StyledCard = styled(Card)(() => ({
  '&': {
    width: '100%',
  },
  '& .title': {
    width: '100%',
  },

  '&.notistack-MuiContent-success': {
    borderLeft: '12px solid rgba(150, 210, 123, 0.8)',
    '& .icon': {
      color: 'rgba(150, 210, 123, 0.8)',
    },
  },
  '&.notistack-MuiContent-error': {
    borderLeft: '12px solid rgba(255, 72, 72, 0.8)',
    '& .icon': {
      color: 'rgba(255, 72, 72, 0.8)',
    },
  },
  '&.notistack-MuiContent-warning': {
    borderLeft: '12px solid rgba(255, 190, 45, 0.8)',
    '& .icon': {
      color: 'rgba(255, 190, 45, 0.8)',
    },
  },
  '&.notistack-MuiContent-info': {
    borderLeft: '12px solid rgba(2, 136, 209, 0.8)',
    '& .icon': {
      color: 'rgba(2, 136, 209, 0.8)',
    },
  },
  '&.notistack-MuiContent-custom': {
    borderLeft: '12px solid rgba(21, 31, 87, 1)',
    '& .icon': {
      color: 'rgba(21, 31, 87, 1)',
    },
  },
  '& .MuiCardContent-root': {
    padding: '8px 16px 8px 16px !important',
  },
}));

const MessageContent = ({ message }) => {
  if (isValidElement(message)) return message;

  return (
    <Typography sx={{ fontWeight: 400, color: '#7A7B84' }} variant="subtitle2">
      {message}
    </Typography>
  );
};

const Snackbar = forwardRef(({ id, variant = 'success', ...props }, ref) => {
  const { closeSnackbar } = useSnackbar();

  const handleDismiss = useCallback(
    (event) => {
      event.stopPropagation();
      closeSnackbar(id);
    },
    [id, closeSnackbar]
  );

  const Icon = IconVariants[variant] ?? variant;
  const title = props?.title ?? TitleVariants[variant];
  return (
    <SnackbarContent ref={ref} style={{ minWidth: '377px', maxWidth: '600px', right: 0 }} className="aababa">
      <StyledCard className={`notistack-MuiContent-${variant}`}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#fff' }}>
          <Stack direction={'row'} width={'100%'} flex={1} justifyContent={'center'} alignItems={'center'} spacing={2}>
            {Icon && (
              <SvgIcon className="icon" component={Icon} sx={{ width: 40, height: 40, mt: 0.5 }} inheritViewBox />
            )}
            <Stack spacing={0.5} width={'inherit'}>
              {title && (
                <Typography variant="subtitle1" sx={{ lineHeight: '30px' }}>
                  {title}
                </Typography>
              )}
              <MessageContent message={props.message} />
            </Stack>
            <IconButton size="small" onClick={handleDismiss} sx={{ alignSelf: 'start' }}>
              <CloseIcon sx={{ color: '#7A7B84' }} fontSize="small" />
            </IconButton>
          </Stack>
        </CardContent>
      </StyledCard>
    </SnackbarContent>
  );
});

Snackbar.displayName = 'CustomSnackbar';

export default Snackbar;
